import { Suspense } from "react";
import { twMerge } from "tailwind-merge";

import { richTextToMultilineText } from "@lib/noteText";
import { lazyWithBackgroundWarm } from "@client/utils/backgroundChunkWarming";

import type { RichTextEditorProps } from "./RichTextEditorImpl";

// Public face of the notes editor. The editing machinery — lexical and its
// @lexical/* plugins — is a six-figure-bytes startup parse that no landing
// page needs for first paint, so it lives in `./RichTextEditorImpl`, loaded
// through lazy() below (same shape as MarkdownViewer/MarkdownViewerImpl).
// While the impl chunk loads (first render only; the background chunk warm
// usually has it cached within seconds of startup) the fallback shows the
// note's plain text inside the editor's exact frame, so content is readable
// immediately, nothing shifts when the editor mounts, and a click a moment
// later lands in a live editor.
const LazyRichTextEditor = lazyWithBackgroundWarm(async () => ({
  default: (await import("./RichTextEditorImpl")).RichTextEditorImpl,
}));

/**
 * Read-only stand-in matching the editor's rendered frame: toolbar strip
 * (same height as ToolbarPlugin's row: h-7 buttons + py-1.5), then the
 * content area with the note projected to plain text.
 */
function RichTextEditorFallback({
  value,
  placeholder,
  className,
  "aria-label": ariaLabel,
}: RichTextEditorProps) {
  // Markdown-aware projection: notes are STORED as markdown now, and the
  // fallback must show visible text, not marker syntax (htmlToPlainText
  // passes markdown through raw).
  const text = richTextToMultilineText(value);
  return (
    <div className={twMerge("flex flex-col", className)}>
      <div className="border-border rounded-md border overflow-hidden">
        <div className="h-10 border-b border-border bg-surface-elevated" />
        <div className="relative min-h-72 w-full bg-surface-base px-3 py-2 text-body-md">
          {text ? (
            <div aria-label={ariaLabel} className="whitespace-pre-wrap">
              {text}
            </div>
          ) : placeholder ? (
            <div className="text-muted-foreground pointer-events-none">
              {placeholder}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function RichTextEditor(props: RichTextEditorProps) {
  return (
    <Suspense fallback={<RichTextEditorFallback {...props} />}>
      <LazyRichTextEditor {...props} />
    </Suspense>
  );
}
